import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faUser,
  faPhone,
  faSpinner,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { useAuth } from "../../../hooks/useAuth";
import { toastSuccess, toastError } from "../../../utils/toast";

const AddSellerModal = ({ isOpen, onClose, onAdd, onUserAdded, shopId }) => {
  const { register, loading: authLoading } = useAuth();

  const [form, setForm] = useState({
    full_name: "",
    phone_number: "",
    email_address: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = () => {
    setForm({
      full_name: "",
      phone_number: "",
      email_address: "",
    });
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone_number") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 9) {
        setForm({ ...form, [name]: numericValue });
      }
    } else {
      setForm({ ...form, [name]: value });
    }

    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  const validatePhoneNumber = (phone) => {
    const cleanPhone = phone.replace(/\D/g, "");
    return cleanPhone.length === 9;
  };

  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!form.full_name.trim()) {
      newErrors.full_name = "Full name is required";
    }

    if (!form.phone_number) {
      newErrors.phone_number = "Phone number is required";
    } else if (!validatePhoneNumber(form.phone_number)) {
      newErrors.phone_number =
        "Please enter exactly 9 digits (e.g., 712345678)";
    }

    if (!form.email_address) {
      newErrors.email_address = "Email address is required";
    } else if (!validateEmail(form.email_address)) {
      newErrors.email_address = "Please enter a valid email address";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const userData = {
      full_name: form.full_name,
      phone: `+255${form.phone_number}`,
      email: form.email_address,
      role_type: "seller",
      business_id: shopId,
      password: "Shop@2026",
      password_confirmation: "Shop@2026",
    };

    try {
      setIsLoading(true);

      const response = await register(userData);

      if (response?.status === "SUCCESS" || response?.status === 201) {
        toastSuccess("Seller created successfully!");

        const sellerData = {
          id: response?.data?.user_id || Date.now(),
          shop_saller_id: response?.data?.shop_seller_id || null,
          username: response?.data?.username || "N/A",
          seller_name: form.full_name,
          phone: `+255${form.phone_number}`,
          email: form.email_address,
          status:
            response?.data?.login_status === "active" ? "Active" : "Inactive",
          added_date: new Date().toISOString().split("T")[0],
          created_at: new Date().toISOString().split("T")[0],
          updated_at: new Date().toISOString().split("T")[0],
        };

        resetForm();
        onClose();

        if (onUserAdded) {
          await onUserAdded();
        }

        if (onAdd) {
          onAdd(sellerData);
        }
      }
    } catch (error) {
      console.error("Failed to create seller:", error);

      if (error?.response?.data?.status === "DUPLICATE_FOUND") {
        const data = error.response.data;
        toastError(
          `User already exists: ${data.data?.full_name} (${data.data?.email})`,
        );
      } else {
        toastError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to create seller. Please try again.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onClose();
    }
  };

  const loading = isLoading || authLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add New Seller"
      maxWidth="sm:max-w-md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-4 w-4 animate-spin"
                />
                Creating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Add Seller
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faUser} className="text-blue-800 h-4 w-4" />
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold">
              Add New Seller
            </p>
            <p className="text-xs text-gray-600">
              Fill in all required fields (*). Seller will be linked to this
              shop.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faUser}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Full Name *
          </label>
          <Input
            name="full_name"
            value={form.full_name}
            onChange={handleChange}
            placeholder="Enter full name"
            disabled={loading}
            className={`border ${
              errors.full_name ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />
          {errors.full_name && (
            <p className="text-red-600 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-600 rounded-full"></span>
              {errors.full_name}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faPhone}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Phone Number *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <span className="text-gray-600 text-sm font-medium">+255</span>
            </div>
            <Input
              name="phone_number"
              value={form.phone_number}
              onChange={handleChange}
              placeholder="712345678"
              maxLength={9}
              disabled={loading}
              className={`border ${
                errors.phone_number ? "border-red-500" : "border-gray-300"
              } rounded-md pl-14 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
            />
          </div>
          <p className="text-xs text-gray-600">
            Enter exactly 9 digits (e.g., 712345678)
          </p>
          {errors.phone_number && (
            <p className="text-red-600 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-600 rounded-full"></span>
              {errors.phone_number}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faEnvelope}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Email *
          </label>
          <Input
            name="email_address"
            type="email"
            value={form.email_address}
            onChange={handleChange}
            placeholder="Enter email address"
            disabled={loading}
            className={`border ${
              errors.email_address ? "border-red-500" : "border-gray-300"
            } rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30`}
          />
          {errors.email_address && (
            <p className="text-red-600 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-600 rounded-full"></span>
              {errors.email_address}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddSellerModal;
